import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { NotificationDto } from '../../../modules/association/models/notification.models';
import { TaskService } from '../../../modules/association/services/task.service';

@Component({
  selector: 'app-notification-item',
  templateUrl: './notification-item.component.html',
  styleUrls: ['./notification-item.component.scss']
})
export class NotificationItemComponent {
  @Input() notification: NotificationDto;
  @Output() notificationClick = new EventEmitter<string>();

  constructor(
    private router: Router,
    private toastrService: ToastrService,
    private translateService: TranslateService,
    private taskService: TaskService
  ) {}

  onNotificationClick(): void {
    this.notificationClick.emit(this.notification.id);

    if (this.notification.actionUrl) {
      // Check if this is a task notification
      if (this.notification.entityType?.toLowerCase().includes('task') && this.notification.entityId) {
        // Validate task exists before navigating
        this.taskService.exists(this.notification.entityId).subscribe({
          next: (res) => {
            if (res.success && res.value) {
              // Task exists, proceed with navigation
              this.router.navigateByUrl(this.notification.actionUrl);
            } else {
              // Task was deleted, show user-friendly error
              const message = this.translateService.instant('notifications.taskDeleted');
              this.toastrService.error(message, '');
              // Optionally: mark notification as read to prevent repeated clicks
            }
          },
          error: () => {
            // On error, still allow navigation (fallback behavior)
            this.router.navigateByUrl(this.notification.actionUrl);
          }
        });
      } else {
        // Not a task notification, proceed normally
        this.router.navigateByUrl(this.notification.actionUrl);
      }
    }
  }

  getMessage(): string {
    const lang = document.documentElement.lang || 'en';
    return lang === 'ar' ? this.notification.messageAr : this.notification.messageEn;
  }

  getTitle(): string {
    const lang = document.documentElement.lang || 'en';
    return lang === 'ar' ? this.notification.titleAr : this.notification.titleEn;
  }

  getIconClass(): string {
    const entityType = this.notification.entityType?.toLowerCase() || '';
    if (entityType.includes('task')) {
      return 'icon-task';
    } else if (entityType.includes('event')) {
      return 'icon-event';
    } else if (entityType.includes('agreement')) {
      return 'icon-agreement';
    } else if (entityType.includes('partner')) {
    } else if (entityType.includes('meeting')) {
      return 'icon-meeting';
    } else if (entityType.includes('donation')) {
      return 'icon-donation';
    } else if (entityType.includes('campaign')) {
      return 'icon-campaign';
    } else if (entityType.includes('volunteer')) {
      return 'icon-volunteer';
    }
    return 'icon-default';
  }

  getIconPath(): string {
    const entityType = this.notification.entityType?.toLowerCase() || '';
    if (entityType.includes('task')) {
      return 'M9 2a1 1 0 000 2h2a1 1 0 100-2H9z M6 5a1 1 0 00-1 1v1a1 1 0 002 0V6h1v9a1 1 0 102 0V6h4v1a1 1 0 102 0V6a1 1 0 00-1-1H6z';
    } else if (entityType.includes('event')) {
      return 'M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 011 1v1a1 1 0 11-2 0V8a1 1 0 011-1zm8 0a1 1 0 011 1v1a1 1 0 11-2 0V8a1 1 0 011-1zm-8 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zm8 0a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1z';
    } else if (entityType.includes('agreement')) {
      return 'M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V8h-3a2 2 0 01-2-2V4H6z';
    } else if (entityType.includes('partner')) {
      return 'M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z';
    } else if (entityType.includes('meeting')) {
      return 'M10 2a8 8 0 100 16 8 8 0 000-16zm1 12H9v-2h2v2zm0-3H9V5h2v6z';
    } else if (entityType.includes('donation')) {
      return 'M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5';
    } else if (entityType.includes('campaign')) {
      return 'M15.042 21.672L13.684 16.6m0 0l-2.51 2.225.569-9.47 5.227 7.917-3.286-.672zM12 2.25V4.5m5.834-1.666l-1.334 1.334M2.25 12h2.25m14.25 0h2.25M3.917 8.834l1.334-1.334m0 0L8.25 2.25M4.5 18l1.334 1.334M12 2.25a9.75 9.75 0 11-9.75 9.75 9.75 9.75 0 019.75-9.75z';
    } else if (entityType.includes('volunteer')) {
      return 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z';
    }
    return 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
  }
}
